import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { InputNumber, Slider } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVPrimitive, RolodexEntryPatchJson } from 'types'

const MetricsSection = styled.div`
  margin-top: 8px;
`

const MetricsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  font-weight: 500;
`

const MetricsContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
`

const MetricItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  gap: 8px;
`

const MetricLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  min-width: 40px;
  font-size: 0.75rem;
`

const SliderContainer = styled.div`
  flex: 1;
  min-width: 80px;
`

const StyledInputNumber = styled(InputNumber)`
  width: 70px;

  .ant-input-number-input {
    text-align: right;
    font-size: 0.75rem;
  }
`

const RotationButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
`

const RotationButton = styled.button`
  padding: 0.25rem 0.75rem;
  border: 1px solid #666666;
  background-color: #000000;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.7rem;
  flex: 1;

  &:hover:not(:disabled) {
    background-color: #444444;
    border-color: #888888;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const ROTATION_LABELS = ['Yaw', 'Pitch', 'Roll'] as const

interface RotationSectionProps {
  selectedPrimitive: KVPrimitive
}

const getRotationArray = (rotation: unknown): number[] => {
  if (Array.isArray(rotation) && rotation.length === 3) {
    return rotation.map(Number)
  }
  return [0, 0, 0]
}

const rotationsAreEqual = (a: number[], b: number[]): boolean => {
  return a.every((val, idx) => Math.abs(val - b[idx]) < 0.001)
}

export const RotationSection: React.FC<RotationSectionProps> = ({
  selectedPrimitive,
}) => {
  const { setSelectedMarkup } = useKestrelViewerContext()
  const { kestrelModel, primitives, setPrimitives, sendKVCommand } =
    useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const [isRotationOpen, setIsRotationOpen] = useState(false)
  const [savedRotation, setSavedRotation] = useState<number[]>(() =>
    getRotationArray(selectedPrimitive.transform.rotation)
  )
  const [localRotation, setLocalRotation] = useState<number[]>(() =>
    getRotationArray(selectedPrimitive.transform.rotation)
  )
  const [isSaving, setIsSaving] = useState(false)

  const primitiveRef = useRef(selectedPrimitive)
  primitiveRef.current = selectedPrimitive

  const primitivesRef = useRef(primitives)
  primitivesRef.current = primitives

  const initialRotation = selectedPrimitive.transform.rotation
  const canEditRotation =
    Array.isArray(initialRotation) && initialRotation.length === 3

  const prevIdRef = useRef(selectedPrimitive.id)

  useEffect(() => {
    if (prevIdRef.current !== selectedPrimitive.id) {
      prevIdRef.current = selectedPrimitive.id
      if (canEditRotation) {
        const rotation = getRotationArray(selectedPrimitive.transform.rotation)
        setLocalRotation(rotation)
        setSavedRotation(rotation)
      }
    }
  }, [
    selectedPrimitive.id,
    selectedPrimitive.transform.rotation,
    canEditRotation,
  ])

  const hasChanges = useMemo(
    () => !rotationsAreEqual(localRotation, savedRotation),
    [localRotation, savedRotation]
  )

  const applyRotationToViewer = useCallback(
    (newRotation: number[]) => {
      const currentPrimitive = primitiveRef.current
      const currentPrimitives = primitivesRef.current

      if (!sendKVCommand || !currentPrimitives) return

      const newTransform = {
        ...currentPrimitive.transform,
        rotation: newRotation,
      }

      // Send KV command for live preview
      sendKVCommand({
        command: KestrelCommands.UPDATE_PRIMITIVE,
        primitiveId: currentPrimitive.id,
        transform: newTransform,
      })

      // Helper to clone primitive with new transform
      const clonePrimitiveWithTransform = (p: KVPrimitive) =>
        Object.assign(Object.create(Object.getPrototypeOf(p)), p, {
          transform: newTransform,
        })

      // Update primitives list
      setPrimitives(
        currentPrimitives.map((p) =>
          p.id === currentPrimitive.id ? clonePrimitiveWithTransform(p) : p
        )
      )

      // Update selected markup
      setSelectedMarkup(clonePrimitiveWithTransform(currentPrimitive))
    },
    [sendKVCommand, setPrimitives, setSelectedMarkup]
  )

  const handleRotationChange = useCallback(
    (index: number, value: number | string | null) => {
      if (value === null) return

      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(numValue)) return

      setLocalRotation((prev) => {
        const newRotation = [...prev]
        newRotation[index] = numValue
        // Apply to viewer with the new values
        applyRotationToViewer(newRotation)
        return newRotation
      })
    },
    [applyRotationToViewer]
  )

  const handleReset = useCallback(() => {
    const resetRotation = [...savedRotation]
    setLocalRotation(resetRotation)
    applyRotationToViewer(resetRotation)
  }, [savedRotation, applyRotationToViewer])

  const handleSave = useCallback(async () => {
    const currentPrimitives = primitivesRef.current
    const currentPrimitive = primitiveRef.current

    if (!kestrelModel?.id || !currentPrimitives || isSaving) return

    setIsSaving(true)

    const newTransform = {
      ...currentPrimitive.transform,
      rotation: localRotation,
    }

    const updatedPrimitives = currentPrimitives.map((p) =>
      p.id === currentPrimitive.id
        ? Object.assign(Object.create(Object.getPrototypeOf(p)), p, {
            transform: newTransform,
          })
        : p
    )

    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        primitives: updatedPrimitives.map((p) => KVPrimitive.toJSON(p)),
      },
    }

    try {
      await rolodexPatch([patchData])
      setSavedRotation([...localRotation])
    } catch (error) {
      console.error('Failed to save rotation:', error)
    } finally {
      setIsSaving(false)
    }
  }, [kestrelModel?.id, localRotation, rolodexPatch, isSaving])

  // Prevent keyboard events from propagating when typing in input
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
  }

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleInputMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleInputMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  if (!canEditRotation) return null

  return (
    <MetricsSection>
      <MetricsHeader onClick={() => setIsRotationOpen((prev) => !prev)}>
        {isRotationOpen ? (
          <ChevronDownIcon width={16} height={16} />
        ) : (
          <ChevronRightIcon width={16} height={16} />
        )}
        <span>Rotation</span>
      </MetricsHeader>
      <MetricsContent $isOpen={isRotationOpen}>
        <MetricsList>
          {ROTATION_LABELS.map((label, index) => (
            <MetricItem key={label}>
              <MetricLabel>{label}</MetricLabel>
              <SliderContainer>
                <Slider
                  value={localRotation[index]}
                  min={-180}
                  max={180}
                  step={1}
                  onChange={(value) => handleRotationChange(index, value)}
                />
              </SliderContainer>
              <StyledInputNumber
                value={localRotation[index]}
                min={-180}
                max={180}
                step={1}
                precision={1}
                suffix='°'
                onChange={(value) => handleRotationChange(index, value)}
                onKeyDown={handleInputKeyDown}
                onClick={handleInputClick}
                onMouseDown={handleInputMouseDown}
                onMouseUp={handleInputMouseUp}
                controls={false}
              />
            </MetricItem>
          ))}
        </MetricsList>
        <RotationButtonRow>
          <RotationButton
            onClick={handleReset}
            disabled={!hasChanges || isSaving}>
            Reset
          </RotationButton>
          <RotationButton
            onClick={handleSave}
            disabled={!hasChanges || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </RotationButton>
        </RotationButtonRow>
      </MetricsContent>
    </MetricsSection>
  )
}
